import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Gauge } from 'lucide-react'
import api from '../services/api'
import MidDayResetCheckInModal from '../components/MidDayResetCheckInModal'
import EmailOtpModal from '../components/EmailOtpModal'
import { saveAuth, getToken } from '../utils/userData'

// Helper to format date as YYYYMMDD integer for session number
const getDateAsSessionNumber = (date = new Date()) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return parseInt(`${year}${month}${day}`)
}

// Helper to check if today is a weekday (Mon-Fri)
const isWeekday = (date = new Date()) => {
  const day = date.getDay()
  return day >= 1 && day <= 5
}

// Helper to get next weekday if today is weekend
const getNextWeekday = (date = new Date()) => {
  const result = new Date(date)
  const day = result.getDay()
  if (day === 0) { // Sunday
    result.setDate(result.getDate() + 1)
  } else if (day === 6) { // Saturday
    result.setDate(result.getDate() + 2)
  }
  return result
}

// Helper to format date for display
const formatDateForDisplay = (date = new Date()) => {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

const scoreLabels = {
  energyLevel: { label: 'Energy Level', emoji: '⚡', color: 'from-yellow-400 to-orange-500' },
  stressLevel: { label: 'Stress Level', emoji: <Gauge className="inline w-10 h-10" />, color: 'from-red-400 to-pink-500' },
  emotionalState: { label: 'Emotional State', emoji: '💜', color: 'from-purple-400 to-indigo-500' },
  physicalState: { label: 'Physical State', emoji: '💪', color: 'from-green-400 to-teal-500' },
}

const MidDayResetCheckIn = () => {
  const [loading, setLoading] = useState(true)
  const [userSignup, setUserSignup] = useState(null)
  const [checkIns, setCheckIns] = useState([])
  const [checkInModal, setCheckInModal] = useState({ isOpen: false, type: null })
  const [emailModal, setEmailModal] = useState({ isOpen: false })

  // Get today's date or next weekday
  const today = new Date()
  const sessionDate = isWeekday(today) ? today : getNextWeekday(today)
  const sessionNumber = getDateAsSessionNumber(sessionDate)
  const displayDate = formatDateForDisplay(sessionDate)
  const isToday = isWeekday(today)

  useEffect(() => {
    checkAuthAndFetchData()
  }, [])

  const checkAuthAndFetchData = async () => {
    setLoading(true)
    const token = getToken()

    if (token) {
      try {
        // Try to get enrollment - if 404, auto-enroll
        const meResponse = await api.get('/api/programs/mid-day-reset/enrollment')
        if (meResponse.data.success) {
          const { user, enrollment } = meResponse.data.data
          setUserSignup({
            id: user.id,
            name: user.name,
            email: user.email,
            ...enrollment?.data,
          })
          fetchCheckIns()
          // Submit any pending check-ins after auth
          await submitPendingCheckIns()
        }
      } catch (error) {
        // If 404 (not enrolled), try to auto-enroll
        if (error.response?.status === 404) {
          await autoEnrollUser()
        }
        // Other errors - token invalid, handled by interceptor
      }
    }
    setLoading(false)
  }

  // Auto-enroll user in the program
  const autoEnrollUser = async () => {
    try {
      const enrollResponse = await api.post('/api/programs/mid-day-reset/auto-enroll', {})
      if (enrollResponse.data.success) {
        const { user, enrollment } = enrollResponse.data.data
        setUserSignup({
          id: user.id,
          name: user.name,
          email: user.email,
          ...enrollment?.data,
        })
        fetchCheckIns()
        // Submit any pending check-ins after enrollment
        await submitPendingCheckIns()
      }
    } catch (error) {
      console.error('Error auto-enrolling:', error)
    }
  }

  // Submit pending check-ins stored in localStorage
  const submitPendingCheckIns = async () => {
    const pendingCheckIns = JSON.parse(localStorage.getItem('pendingMidDayResetCheckIns') || '[]')
    if (pendingCheckIns.length === 0) return

    for (const checkIn of pendingCheckIns) {
      try {
        await api.post('/api/programs/mid-day-reset/check-ins', {
          sessionNumber: checkIn.sessionNumber,
          checkInType: checkIn.checkInType,
          energyLevel: checkIn.energyLevel,
          stressLevel: checkIn.stressLevel,
          emotionalState: checkIn.emotionalState,
          physicalState: checkIn.physicalState,
          heartRate: checkIn.heartRate,
        })
        
        // If there's feedback data, submit that too
        if (checkIn.feedback) {
          await api.post('/api/programs/mid-day-reset/feedback', {
            sessionNumber: checkIn.sessionNumber,
            ...checkIn.feedback,
            facilitatorRating: 5, // Default
          })
        }
      } catch (error) {
        console.error('Error submitting pending check-in:', error)
      }
    }

    // Clear pending check-ins after submission
    localStorage.removeItem('pendingMidDayResetCheckIns')
    
    // Refresh check-ins
    fetchCheckIns()
  }

  const fetchCheckIns = async () => {
    try {
      const response = await api.get('/api/programs/mid-day-reset/check-ins')
      if (response.data.success) {
        setCheckIns(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching check-ins:', error)
    }
  }

  const hasPreEventCheckIn = (num) => checkIns.some(
    (ci) => ci.sessionNumber === num && ci.checkInType === 'pre-event'
  )
  const hasPostEventCheckIn = (num) => checkIns.some(
    (ci) => ci.sessionNumber === num && ci.checkInType === 'post-event'
  )

  const getCheckIn = (num, type) => checkIns.find(
    (ci) => ci.sessionNumber === num && ci.checkInType === type
  )

  const openCheckInModal = (type) => {
    setCheckInModal({ isOpen: true, type })
  }

  const closeCheckInModal = () => {
    setCheckInModal({ isOpen: false, type: null })
  }

  const handleCheckInSuccess = () => {
    const token = getToken()
    if (token) {
      fetchCheckIns()
    }
  }

  // Handle unauthenticated check-in completion
  const handleUnauthenticatedComplete = (checkInData) => {
    const pendingCheckIns = JSON.parse(localStorage.getItem('pendingMidDayResetCheckIns') || '[]')
    const newCheckIn = {
      ...checkInData,
      sessionNumber,
      checkInType: checkInModal.type,
      timestamp: new Date().toISOString(),
    }
    
    const existingIndex = pendingCheckIns.findIndex(
      ci => ci.sessionNumber === sessionNumber && ci.checkInType === checkInModal.type
    )
    
    if (existingIndex >= 0) {
      pendingCheckIns[existingIndex] = newCheckIn
    } else {
      pendingCheckIns.push(newCheckIn)
    }
    
    localStorage.setItem('pendingMidDayResetCheckIns', JSON.stringify(pendingCheckIns))
    
    closeCheckInModal()
    setEmailModal({ isOpen: true })
  }

  const closeEmailModal = () => {
    setEmailModal({ isOpen: false })
  }

  const getPendingCheckIn = (type) => {
    const pendingCheckIns = JSON.parse(localStorage.getItem('pendingMidDayResetCheckIns') || '[]')
    return pendingCheckIns.find(ci => ci.sessionNumber === sessionNumber && ci.checkInType === type)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-100 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full"
        />
      </div>
    )
  }

  const hasBothCheckIns = hasPreEventCheckIn(sessionNumber) && hasPostEventCheckIn(sessionNumber)
  const hasPendingPre = getPendingCheckIn('pre-event')
  const hasPendingPost = getPendingCheckIn('post-event')

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-100">
      <div className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex justify-center"
          >
            <Link to="/">
              <img
                src="/logo.png"
                alt="Wellness Loop Logo"
                className="h-16 w-auto"
              />
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="mb-4">
              <span className="inline-block text-2xl md:text-3xl font-black tracking-tight">
                <span className="text-gray-900">build</span>
                <span className="text-teal-600">3</span>
              </span>
              <span className="ml-2 text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                MID-DAY RESET
              </span>
            </div>
            <p className="text-lg text-teal-600 font-semibold">
              Breathwork & Meditation Check-In
            </p>
          </motion.div>

          {/* Session Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-50 rounded-3xl shadow-xl p-8 border-2 border-teal-200"
          >
            <div className="flex flex-col items-center text-center">
              <div className="mb-4">
                <span className="text-sm font-semibold text-teal-700 uppercase tracking-wide">
                  {isToday ? "Today's Session" : 'Next Session'}
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                Mid-Day Reset
              </h2>
              <p className="text-lg text-gray-700 mb-4 italic">
                30 minutes of breathwork & meditation
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 text-gray-600 mb-6">
                <div className="flex items-center">
                  <span className="text-xl mr-2">📅</span>
                  <span className="font-semibold">{displayDate}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-xl mr-2">⏰</span>
                  <span className="font-semibold">1:00 PM - 1:30 PM</span>
                </div>
                <div className="flex items-center">
                  <span className="text-xl mr-2">📍</span>
                  <span className="font-semibold">Online</span>
                </div>
              </div>

              {/* Schedule info */}
              <div className="bg-white/60 rounded-xl px-6 py-3 mb-6 border border-teal-100">
                <p className="text-gray-600 text-sm">
                  <span className="font-semibold text-teal-700">Ongoing:</span> Monday to Friday, 1:00 - 1:30 PM
                </p>
              </div>

              {/* Check-In Buttons */}
              <div className="flex flex-wrap justify-center gap-3 mb-4">
                <button
                  onClick={() => openCheckInModal('pre-event')}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                    hasPreEventCheckIn(sessionNumber)
                      ? 'bg-green-100 text-green-700 border-2 border-green-300'
                      : hasPendingPre
                      ? 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  {hasPreEventCheckIn(sessionNumber) 
                    ? '✓ Pre-Session Check-In Complete' 
                    : hasPendingPre
                    ? '⏳ Pre-Session Pending Login'
                    : 'Pre-Session Check-In'}
                </button>
                <button
                  onClick={() => openCheckInModal('post-event')}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                    hasPostEventCheckIn(sessionNumber)
                      ? 'bg-green-100 text-green-700 border-2 border-green-300'
                      : hasPendingPost
                      ? 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300'
                      : 'bg-cyan-600 text-white hover:bg-cyan-700'
                  }`}
                >
                  {hasPostEventCheckIn(sessionNumber) 
                    ? '✓ Post-Session Check-In Complete' 
                    : hasPendingPost
                    ? '⏳ Post-Session Pending Login'
                    : 'Post-Session Check-In'}
                </button>
              </div>

              {/* Pending check-in message */}
              {(hasPendingPre || hasPendingPost) && !userSignup && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <span className="font-medium">Note:</span> You have pending check-ins. 
                    <button 
                      onClick={() => setEmailModal({ isOpen: true })}
                      className="ml-1 text-yellow-900 underline hover:no-underline"
                    >
                      Sign in or sign up
                    </button> to save them.
                  </p>
                </div>
              )}
            </div>

            {/* Score Comparison - Show when both check-ins are done */}
            {userSignup && hasBothCheckIns && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-6 pt-6 border-t border-teal-200"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center justify-center">
                  <span className="mr-2">📊</span>
                  Your Session Results
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(scoreLabels).map(([key, { label, emoji }]) => {
                    const preCheckIn = getCheckIn(sessionNumber, 'pre-event')
                    const postCheckIn = getCheckIn(sessionNumber, 'post-event')
                    const preValue = preCheckIn?.[key] || 0
                    const postValue = postCheckIn?.[key] || 0
                    const diff = postValue - preValue
                    const isStress = key === 'stressLevel'
                    const isImproved = isStress ? diff < 0 : diff > 0
                    const isDeclined = isStress ? diff > 0 : diff < 0

                    return (
                      <div
                        key={key}
                        className="bg-white/80 backdrop-blur rounded-xl p-4 shadow-sm"
                      >
                        <div className="flex items-center justify-center mb-2">
                          <span className="text-2xl">{emoji}</span>
                        </div>
                        <div className="text-center text-xs font-medium text-gray-600 mb-2">{label}</div>
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-lg font-semibold text-gray-500">{preValue}</span>
                          <span className="text-gray-400">→</span>
                          <span className="text-lg font-bold text-gray-800">{postValue}</span>
                        </div>
                        <div className="text-center mt-2">
                          <span
                            className={`inline-block px-2 py-1 rounded-full text-xs font-bold ${
                              isImproved
                                ? 'bg-green-100 text-green-700'
                                : isDeclined
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {diff > 0 ? '+' : ''}{diff}
                            {isImproved && ' ↑'}
                            {isDeclined && ' ↓'}
                            {diff === 0 && ' →'}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Summary message */}
                {(() => {
                  const preCheckIn = getCheckIn(sessionNumber, 'pre-event')
                  const postCheckIn = getCheckIn(sessionNumber, 'post-event')
                  const improvements = Object.keys(scoreLabels).filter(key => {
                    const diff = (postCheckIn?.[key] || 0) - (preCheckIn?.[key] || 0)
                    return key === 'stressLevel' ? diff < 0 : diff > 0
                  })
                  
                  if (improvements.length >= 3) {
                    return (
                      <div className="mt-4 flex items-center justify-center text-green-700 bg-green-100/80 rounded-lg px-4 py-3">
                        <span className="text-xl mr-2">🎉</span>
                        <span className="font-medium">Amazing! You showed improvement in {improvements.length} wellness areas!</span>
                      </div>
                    )
                  } else if (improvements.length > 0) {
                    return (
                      <div className="mt-4 flex items-center justify-center text-teal-700 bg-teal-100/80 rounded-lg px-4 py-3">
                        <span className="text-xl mr-2">✨</span>
                        <span className="font-medium">Great progress! You improved in {improvements.length} area{improvements.length > 1 ? 's' : ''}.</span>
                      </div>
                    )
                  } else {
                    return (
                      <div className="mt-4 flex items-center justify-center text-gray-600 bg-gray-100/80 rounded-lg px-4 py-3">
                        <span className="text-xl mr-2">💭</span>
                        <span className="font-medium">Every session is a step forward. Keep up the wellness journey!</span>
                      </div>
                    )
                  }
                })()}
              </motion.div>
            )}
          </motion.div>

          {/* Navigation Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 flex flex-wrap justify-center gap-4"
          >
            <Link
              to="/mid-day-reset/dashboard"
              className="px-6 py-3 bg-white text-teal-600 font-semibold rounded-xl shadow-md hover:shadow-lg transition-all border border-teal-200"
            >
              View Dashboard
            </Link>
            {!userSignup && (
              <button
                onClick={() => setEmailModal({ isOpen: true })}
                className="px-6 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all"
              >
                Sign In / Sign Up
              </button>
            )}
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center mt-8 text-gray-500"
          >
            <p>Build3 Mid-Day Reset</p>
            <p className="text-sm mt-2">Powered by Wellness Loop</p>
          </motion.div>
        </div>
      </div>

      {/* Check-In Modal */}
      {checkInModal.isOpen && (
        <MidDayResetCheckInModal
          isOpen={checkInModal.isOpen}
          onClose={closeCheckInModal}
          checkInDate={sessionDate}
          checkInType={checkInModal.type}
          onSuccess={handleCheckInSuccess}
          isAuthenticated={!!userSignup}
          onUnauthenticatedComplete={handleUnauthenticatedComplete}
        />
      )}

      {/* Email Modal for Login/Signup */}
      <EmailOtpModal
        isOpen={emailModal.isOpen}
        onClose={closeEmailModal}
        title="Mid-Day Reset"
        emoji="🧘"
        emailDescription="Breathwork & Meditation - Enter your email to continue"
        emailHint="We'll send you a verification code to your email."
        allowNewUserRegistration={true}
        registerDescription="Complete your registration to save your check-in"
        requestOtp={async (email) => {
          // Always create user if not exists (for new user flow)
          const response = await api.post('/api/auth/otp', {
            email,
            programSlug: 'mid-day-reset',
            createIfNotExists: true,
          })
          return {
            success: response.data.success,
            userExists: response.data.data?.userExists,
            code: response.data.data?.code,
          }
        }}
        verifyOtp={async (email, code) => {
          const response = await api.post('/api/auth/verify-otp', {
            email,
            code,
            programSlug: 'mid-day-reset',
          })
          return response.data
        }}
        registerNewUser={async (email, name, phone) => {
          const response = await api.post('/api/auth/complete-registration', {
            email,
            name,
            phone,
            programSlug: 'mid-day-reset',
          })
          return response.data
        }}
        onSuccess={(data) => {
          const { token } = data
          saveAuth(token, null)  // Just save token, user data will be fetched
          closeEmailModal()
          checkAuthAndFetchData()
        }}
      />
    </div>
  )
}

export default MidDayResetCheckIn
